import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';

import { functions } from '../firebase';
import { canUseAiSupport } from '../shared/abilities';
import ModalButton from './ModalButton';

const askAiSupport = functions.httpsCallable('askAiSupport');

// 現在のルートから画面コンポーネント名を推定する（ベストエフォート）。
// パス末尾セグメントを手がかりにするだけの軽い推定で、確定はサーバ側がコードを見て行う。
const guessComponentName = (pathname) => {
  const seg = pathname.split('/').filter(Boolean).pop() || '';
  if (!seg || /^[0-9a-f-]{8,}$/i.test(seg)) return null; // IDっぽいものは除外
  return seg;
};

export default function AiSupportButton(props) {
  const { user } = props;
  if (!canUseAiSupport(user)) return null;

  return (
    <ModalButton
      Component={Button}
      color="link"
      title="AIサポート"
      modalProps={{ size: 'lg', scrollable: true }}
      content={_ => <ChatContent user={user} />}
    >
      <span className="fas fa-robot mr-md-1" />
      <span className="d-none d-md-inline">AIサポート</span>
    </ModalButton>
  );
}

function ChatContent({ user }) {
  const location = useLocation();
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content }
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    const history = messages; // 送信前の履歴（ステートレス: このセッション内のみ）
    setMessages([...messages, { role: 'user', content: question }]);
    setInput('');
    setError(null);
    setIsLoading(true);

    try {
      const { data } = await askAiSupport({
        question,
        history,
        context: {
          currentPath: location.pathname,
          componentName: guessComponentName(location.pathname),
          country: user?.salesCountry,
          tenantId: user?.tenantId,
        },
      });
      setMessages(_ => [..._, { role: 'assistant', content: data.answer }]);
    } catch (err) {
      setError(err.message || 'エラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column" style={{ minHeight: '50vh' }}>
      <div className="text-muted small mb-2">
        画面の使い方や機能について質問できます（例: 「この画面の削除ボタンを押すとどうなりますか？」）
      </div>

      <div className="flex-grow-1 mb-3">
        {
          messages.length === 0 && !isLoading && (
            <div className="text-center text-muted py-4">
              質問を入力してください。
            </div>
          )
        }
        {
          messages.map((message, i) => (
            <div key={i} className={`d-flex mb-2 ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
              <div
                className="p-2 rounded border bg-white"
                style={{ maxWidth: '85%', whiteSpace: message.role === 'user' ? 'pre-wrap' : undefined }}
              >
                {
                  message.role === 'assistant'
                    ? <div className="ai-support-markdown"><ReactMarkdown>{message.content}</ReactMarkdown></div>
                    : message.content
                }
              </div>
            </div>
          ))
        }
        {
          isLoading && (
            <div className="d-flex justify-content-start mb-2">
              <div className="p-2 rounded bg-light border text-muted">
                <span className="fas fa-spinner fa-spin mr-2" />
                コードを確認しています…
              </div>
            </div>
          )
        }
        {
          error && (
            <div className="alert alert-danger py-2 mb-2">{error}</div>
          )
        }
        <div ref={bottomRef} />
      </div>

      <form onSubmit={onSubmit} className="d-flex align-items-end">
        <textarea
          className="form-control mr-2"
          placeholder="質問を入力…（Enterで送信 / Shift+Enterで改行）"
          rows={2}
          value={input}
          onChange={_ => setInput(_.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              onSubmit(e);
            }
          }}
          disabled={isLoading}
          style={{ resize: 'none' }}
        />
        <Button type="submit" color="primary" className="text-nowrap" style={{ minWidth: 64 }} disabled={isLoading || !input.trim()}>
          送信
        </Button>
      </form>
    </div>
  );
}
